// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';

import { CONSENT_EVENT, getConsentState } from './consentState';

// React hook: `true` while the visitor has granted `category`, re-rendering when
// they change their choice. SSR-safe — returns `false` on the server and until
// the first consent is applied on the client, so it's safe to gate client-only
// trackers with it and there's no hydration mismatch (server and first client
// render agree on `false`).
//
//   const analytics = useConsent('analytics')
//   return analytics ? <Script src="…" /> : null
//
// Gate-only by design: mounting a script when the flag flips true loads it after
// consent; unmounting it stops it loading again but does not scrub cookies it has
// already set. Pair with a cookie purge on withdrawal if that matters.
export const useConsent = (category) => {
  const [granted, setGranted] = useState(() => !!getConsentState()[category]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const sync = () => setGranted(!!getConsentState()[category]);
    sync(); // catch a change between initial render and this effect
    window.addEventListener(CONSENT_EVENT, sync);
    return () => window.removeEventListener(CONSENT_EVENT, sync);
  }, [category]);

  return granted;
};

// Convenience wrapper: renders `children` only once `category` is granted,
// otherwise `fallback` (default nothing).
//
//   <ConsentGate category="marketing"><Script … /></ConsentGate>
export const ConsentGate = ({ category, children, fallback = null }) =>
  useConsent(category) ? children : fallback;

export default useConsent;
